import React, { useCallback, useEffect, useState } from "react";
import { Receipt } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { cn } from "../../../lib/utils";
import { getObjLength } from "../../helpers/utils";

export interface OrderSummaryRow {
  label: string;
  value?: string | null;
  amount: string;
  /** Visually emphasize this row (e.g. applied promo discount). */
  highlight?: boolean;
}

/** Payment data shape for built-in payment summary rows */
export interface PaymentSummaryData {
  no_of_properties?: number;
  sub_amount?: string | number;
  discount?: string | number;
  sales_tax_amount_actual?: string | number;
  recording_amount_actual?: string | number;
  setup_fees_actual?: string | number;
  setup_fees?: string | number;
  total?: string | number;
  upgrade_credit?: string | number;
  sp_exclusive_pricing?: boolean;
  sp_offer_headline?: string;
  payment_plan?: string;
  term_months?: string | number;
  paid_at_closing?: boolean;
}

export interface OrderSummaryModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Header title (e.g. "ORDER SUMMARY") */
  title?: string;
  /** Optional icon - defaults to Receipt */
  icon?: React.ReactNode;
  /** Payment data for built-in summary (used when rows not provided) */
  paymentData?: PaymentSummaryData | null;
  /** Category: "enhanced_monitoring" hides Sales/Local taxes, Recording fees, Setup fees */
  category?: string;
  /** Line items to display (used when paymentData not provided) */
  rows?: OrderSummaryRow[];
  /** Label for the total (e.g. "Total due today") */
  totalLabel?: string;
  /** Total amount to display (defaults to paymentData.total when paymentData provided) */
  totalAmount?: string | number;
  /** Callback when confirm button is clicked */
  onConfirm?: () => void;
  /** Confirm button text */
  confirmButtonText?: string;
  /** Close button text */
  closeButtonText?: string;
  /** Show confirm button */
  showConfirmButton?: boolean;
  /** Show close button */
  showCloseButton?: boolean;
  /** Disable confirm button (e.g. during loading) */
  confirmDisabled?: boolean;
  /** Optional custom content to render instead of/in addition to rows */
  children?: React.ReactNode;
  /** Optional class for the header title (e.g. uppercase tracking) */
  titleClassName?: string;
  /** Optional line below the header title (e.g. plan name) */
  subtitle?: React.ReactNode;
  /** Optional class for the close (outline) button */
  closeButtonClassName?: string;
  /** Optional class for the confirm button (overrides default gradient when fully specified) */
  confirmButtonClassName?: string;
  /** Called when the close button is clicked (before dialog closes) */
  onCloseClick?: () => void;
  /** When true, block overlay / Escape dismiss (same as DialogContent preventDismiss) */
  preventDismiss?: boolean;
  /** Show promo code field with Apply button above the total */
  showPromoCode?: boolean;
  promoCodeValue?: string;
  onPromoCodeChange?: (value: string) => void;
  onApplyPromoCode?: () => void;
  onRemovePromoCode?: () => void;
  showRemovePromoCode?: boolean;
  promoApplying?: boolean;
  promoMessage?: string;
  promoMessageIsError?: boolean;
}

export interface OrderSummaryBreakdownProps {
  paymentData?: PaymentSummaryData | null;
  category?: string;
  rows?: OrderSummaryRow[];
  totalLabel?: string;
  totalAmount?: string | number;
  className?: string;
  /** When false, render line items only (caller can insert promo before total). */
  includeTotal?: boolean;
  /** Show promo code field between rows and total (same as Order Summary modal). */
  showPromoCode?: boolean;
  promoCodeValue?: string;
  onPromoCodeChange?: (value: string) => void;
  onApplyPromoCode?: () => void;
  onRemovePromoCode?: () => void;
  showRemovePromoCode?: boolean;
  promoApplying?: boolean;
  promoMessage?: string;
  promoMessageIsError?: boolean;
}

/** Promo-only discount amount (excludes upgrade credit). */
export const getPromoDiscountAmount = (
  data?: PaymentSummaryData | null,
): number => {
  if (!data) return 0;
  const upgradeCredit = Number(
    String(data.upgrade_credit ?? "0").replace(/,/g, ""),
  );
  const discountTotal = Number(String(data.discount ?? "0").replace(/,/g, ""));
  return Math.max(0, discountTotal - upgradeCredit);
};

export const hasAppliedPromoDiscount = (
  data?: PaymentSummaryData | null,
): boolean => getPromoDiscountAmount(data) > 0;

/** True when data looks like a pricing summary (not an error payload). */
export const isValidPaymentSummaryData = (
  data?: PaymentSummaryData | null,
): boolean => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return false;
  }
  // Explicit pricing fields
  if (
    data.total != null ||
    data.sub_amount != null ||
    data.no_of_properties != null ||
    data.sp_exclusive_pricing === true ||
    data.sales_tax_amount_actual != null ||
    data.recording_amount_actual != null ||
    data.setup_fees_actual != null ||
    data.setup_fees != null
  ) {
    return true;
  }
  // Reject plain API error objects (message/success/error only)
  const keys = Object.keys(data).filter(
    (key) => !["message", "success", "error", "statuscode", "status"].includes(key),
  );
  return keys.length > 0;
};

/**
 * CAR term quotes (payment_plan car_1yr / car_3yr / car_5yr) are priced on the base
 * Yearly plan, so the plan name must come from the quote, not the plan record.
 * The customer sees the term, not the partnership: "Yearly" for a single year,
 * otherwise e.g. "3-Year". Null for any other plan.
 */
export const carTermPlanName = (
  data?: PaymentSummaryData | null,
): string | null => {
  const plan = String(data?.payment_plan ?? "");
  if (!plan.startsWith("car_")) return null;
  const fromPlan = plan.match(/^car_(\d+)yr$/)?.[1];
  const fromMonths = Number(data?.term_months) / 12;
  const years = Number(
    fromPlan ?? (Number.isInteger(fromMonths) && fromMonths > 0 ? fromMonths : 0),
  );
  if (!years) return null;
  return years === 1 ? "Yearly" : `${years}-Year`;
};

const toAmount = (value?: string | number | null) =>
  Number(String(value ?? "0").replace(/,/g, "")) || 0;

/**
 * investors-price-details answers with [{ price_breakdown }]; return it in the
 * summary shape the payment screens read. The multi-property discount is folded
 * into the properties line so "discount" stays the promo, as everywhere else.
 * Any other shape is returned unchanged.
 */
export const investorPriceBreakdownToSummary = (data: any) => {
  if (!Array.isArray(data)) {
    return data;
  }
  const breakdown = data[0]?.price_breakdown;
  if (!breakdown) {
    return null;
  }
  return {
    no_of_properties: breakdown.no_of_properties,
    sub_amount: (
      toAmount(breakdown.subscription_amount_actual) -
      toAmount(breakdown.discount)
    ).toFixed(2),
    discount: toAmount(breakdown.promocode_discount).toFixed(2),
    sales_tax_amount_actual: breakdown.sales_tax_amount_actual,
    recording_amount_actual: breakdown.recording_amount_actual,
    setup_fees_actual: breakdown.setup_fees_actual,
    total: breakdown.total,
  };
};

/** Build payment summary rows from paymentData and category */
export const buildPaymentRows = (
  data: PaymentSummaryData,
  category?: string,
): OrderSummaryRow[] => {
  if (data.sp_exclusive_pricing) {
    const termLabel =
      Number(data.term_months) === 12
        ? "1-year"
        : Number(data.term_months) === 36
          ? "3-year"
          : Number(data.term_months) === 60
            ? "5-year"
            : `${data.term_months}-month`;
    return [
      {
        label: "Complete Protection",
        value:
          data.sp_offer_headline ||
          `${termLabel} protection — service provider exclusive`,
        amount: `$${data.sub_amount ?? data.total ?? 0}`,
      },
    ];
  }

  const upgradeCredit = Number(
    String(data.upgrade_credit ?? "0").replace(/,/g, ""),
  );
  const discountTotal = Number(String(data.discount ?? "0").replace(/,/g, ""));
  const promoDiscount = Math.max(0, discountTotal - upgradeCredit);

  const base = [
    {
      label:
        category === "enhanced_monitoring" ? "Monitoring charge" : "Properties",
      value:
        category === "enhanced_monitoring"
          ? null
          : `${data.no_of_properties ?? 0} × Plan`,
      amount: `$${data.sub_amount ?? 0}`,
    },
    ...(promoDiscount > 0
      ? [
          {
            label: "Discount",
            value: null,
            amount: `-$${promoDiscount.toFixed(2)}`,
            highlight: true,
          },
        ]
      : []),
    ...(upgradeCredit > 0
      ? [
          {
            label: "Enhanced Monitoring upgrade credit",
            value: "Pro-rata unused time",
            amount: `-$${data.upgrade_credit}`,
          },
        ]
      : []),
    ...(category !== "enhanced_monitoring"
      ? [
          {
            label: "Sales / Local taxes",
            value: null,
            amount: `$${data.sales_tax_amount_actual ?? 0}`,
          },
          {
            label: "Recording fees (deposit)",
            value: null,
            amount: `$${data.recording_amount_actual ?? 0}`,
          },
          {
            label: "Setup fees",
            value: null,
            amount: `$${data.setup_fees_actual ?? data.setup_fees ?? 0}`,
          },
        ]
      : []),
  ];
  return base;
};

const formatTotalAmount = (totalAmount: string | number) => {
  if (typeof totalAmount === "number") {
    return `$${totalAmount}`;
  }
  return totalAmount.startsWith("$") ? totalAmount : `$${totalAmount}`;
};

/** Inline pricing rows + optional promo + total (same markup as Order Summary modal body). */
export const OrderSummaryBreakdown = ({
  paymentData,
  category,
  rows: rowsProp,
  totalLabel = "Total due today",
  totalAmount: totalAmountProp,
  className,
  includeTotal = true,
  showPromoCode = false,
  promoCodeValue = "",
  onPromoCodeChange,
  onApplyPromoCode,
  onRemovePromoCode,
  showRemovePromoCode = false,
  promoApplying = false,
  promoMessage,
  promoMessageIsError = false,
}: OrderSummaryBreakdownProps) => {
  const rows =
    paymentData && getObjLength(paymentData)
      ? buildPaymentRows(paymentData, category)
      : Array.isArray(rowsProp)
        ? rowsProp
        : [];
  const totalAmount =
    totalAmountProp ??
    (paymentData?.total != null ? String(paymentData.total) : "0");
  const hasRows = rows.length > 0;
  const showPromoBlock = Boolean(
    showPromoCode && !paymentData?.sp_exclusive_pricing,
  );
  const showTotal = includeTotal || showPromoBlock;
  const canRemovePromo = Boolean(
    showRemovePromoCode && onRemovePromoCode && !promoApplying,
  );

  // Keep promo/total visible even while pricing rows are still hydrating.
  if (
    !hasRows &&
    (paymentData == null || !getObjLength(paymentData)) &&
    !showPromoBlock
  ) {
    return null;
  }

  return (
    <div className={cn("w-full min-w-0", className)}>
      {hasRows ? (
        <div className="space-y-0 rounded-xl border border-border bg-muted/30 overflow-hidden">
          {rows.map((row, i) => (
            <div
              key={`${row.label}-${i}`}
              className={cn(
                "flex items-center justify-between px-4 py-3 text-sm",
                i > 0 ? "border-t border-border/80" : "",
                row.highlight
                  ? "bg-emerald-50/90 border-emerald-200/80 text-emerald-900"
                  : "",
              )}
            >
              <span
                className={cn(
                  row.highlight
                    ? "text-emerald-800 font-medium"
                    : "text-muted-foreground",
                )}
              >
                {row.label}
                {row.value ? (
                  <span
                    className={cn(
                      "font-medium ml-1",
                      row.highlight ? "text-emerald-900" : "text-foreground",
                    )}
                  >
                    {row.value}
                  </span>
                ) : null}
              </span>
              <span
                className={cn(
                  "font-semibold tabular-nums",
                  row.highlight ? "text-emerald-700" : "text-foreground",
                )}
              >
                {row.amount}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {showPromoBlock ? (
        <div className="mt-4 space-y-2">
          <label
            htmlFor="order-summary-promo-code"
            className="text-xs font-medium text-muted-foreground block"
          >
            Promo code
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              id="order-summary-promo-code"
              value={promoCodeValue}
              onChange={(event) => onPromoCodeChange?.(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (!promoApplying && promoCodeValue.trim()) {
                    onApplyPromoCode?.();
                  }
                }
              }}
              placeholder="Enter promo code"
              className="h-10 text-sm bg-background"
              disabled={promoApplying || canRemovePromo}
              autoComplete="off"
            />
            {canRemovePromo ? (
              <Button
                type="button"
                variant="outline"
                onClick={onRemovePromoCode}
                disabled={promoApplying}
                className="h-10 px-5 shrink-0 uppercase text-xs font-bold tracking-wide border-destructive/40 text-destructive hover:bg-destructive/10"
              >
                Remove
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={onApplyPromoCode}
                disabled={promoApplying || !promoCodeValue.trim()}
                className="h-10 px-5 shrink-0 uppercase text-xs font-bold tracking-wide"
              >
                {promoApplying ? "Applying…" : "Apply"}
              </Button>
            )}
          </div>
          {promoMessage ? (
            <p
              className={cn(
                "text-xs",
                promoMessageIsError ? "text-destructive" : "text-emerald-600",
              )}
            >
              {promoMessage}
            </p>
          ) : null}
        </div>
      ) : null}
      {showTotal ? (
        <div className="mt-5 rounded-xl bg-gradient-to-r from-secondary/10 to-cyan-400/10 border border-secondary/20 px-5 py-4 flex items-center justify-between">
          <span className="text-base font-semibold text-foreground">
            {totalLabel}
          </span>
          <span className="text-xl font-bold tabular-nums text-secondary">
            {formatTotalAmount(totalAmount)}
          </span>
        </div>
      ) : null}
    </div>
  );
};

const OrderSummaryModal = ({
  open,
  onOpenChange,
  title = "ORDER SUMMARY",
  icon,
  paymentData,
  category,
  rows: rowsProp,
  totalLabel = "Total due today",
  totalAmount: totalAmountProp,
  onConfirm,
  confirmButtonText = "CONFIRM & PAY",
  closeButtonText = "CLOSE",
  showConfirmButton = true,
  showCloseButton = true,
  confirmDisabled = false,
  children,
  titleClassName,
  subtitle,
  closeButtonClassName,
  confirmButtonClassName,
  onCloseClick,
  preventDismiss = false,
  showPromoCode = false,
  promoCodeValue = "",
  onPromoCodeChange,
  onApplyPromoCode,
  onRemovePromoCode,
  showRemovePromoCode = false,
  promoApplying = false,
  promoMessage,
  promoMessageIsError = false,
}: OrderSummaryModalProps) => {
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsConfirming(false);
    }
  }, [open]);

  const handleConfirm = useCallback(() => {
    if (confirmDisabled || isConfirming || !onConfirm) {
      return;
    }
    setIsConfirming(true);
    onConfirm();
  }, [confirmDisabled, isConfirming, onConfirm]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        preventDismiss={preventDismiss}
        containerClassName="z-111111111"
        className="sm:max-w-lg p-0 gap-0 border-0 overflow-hidden rounded-2xl shadow-xl"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {/* Header */}
        <div className="bg-gradient-to-r from-secondary to-cyan-400 py-5 px-6 flex items-center justify-center gap-3">
          <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
            {icon ?? <Receipt className="h-6 w-6 text-white" />}
          </div>
          <div className="text-center">
            <h2
              className={cn(
                "text-lg font-semibold text-white tracking-tight",
                titleClassName,
              )}
            >
              {title}
            </h2>
            {subtitle != null && subtitle !== "" ? (
              <p className="text-white/90 text-sm font-medium mt-1.5 px-1">
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
        {/* Content */}
        <div className="p-6 md:p-8 bg-background">
          {children ? (
            children
          ) : (
            <>
              <OrderSummaryBreakdown
                paymentData={paymentData}
                category={category}
                rows={rowsProp}
                totalLabel={totalLabel}
                totalAmount={totalAmountProp}
                showPromoCode={showPromoCode}
                promoCodeValue={promoCodeValue}
                onPromoCodeChange={onPromoCodeChange}
                onApplyPromoCode={onApplyPromoCode}
                onRemovePromoCode={onRemovePromoCode}
                showRemovePromoCode={showRemovePromoCode}
                promoApplying={promoApplying}
                promoMessage={promoMessage}
                promoMessageIsError={promoMessageIsError}
              />
            </>
          )}
          <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 justify-center items-center">
            {showCloseButton && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onCloseClick?.();
                  onOpenChange(false);
                }}
                className={cn(
                  "w-full sm:w-auto px-8 py-2.5 text-dark rounded-full font-medium border-2 hover:bg-muted/50",
                  closeButtonClassName,
                )}
              >
                {closeButtonText}
              </Button>
            )}
            {showConfirmButton && onConfirm && (
              <Button
                type="button"
                variant={confirmButtonClassName ? "ghost" : "default"}
                onClick={handleConfirm}
                disabled={confirmDisabled || isConfirming}
                className={cn(
                  "w-full sm:w-auto text-white font-semibold px-8 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all touch-manipulation",
                  confirmButtonClassName ??
                    "bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 ring-2 ring-secondary/20 ring-offset-2",
                )}
              >
                {isConfirming && !confirmDisabled
                  ? "Processing…"
                  : confirmButtonText}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderSummaryModal;
